import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceDot } from 'recharts';
import { PEN_STRENGTHS } from '../utils/constants';
import '../App.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// PK parameters for tirzepatide (Mounjaro)
const F = 0.8; // bioavailability
const ka = 0.0373; // absorption rate constant (1/h)
const tHalf = 5.4 * 24; // half-life in hours
const ke = Math.log(2) / tHalf; // elimination rate constant (1/h)
const Vd = 10.3; // volume of distribution (L)

interface Dose {
  _id: string;
  penStrength: number;
  clicks: number;
  doseAmount: number;
  date: string;
  remainingClicks: number;
  remainingDoses: number;
  penId: string;
  penName: string;
}

function calculateConcentration(doses: Dose[], now: Date): number {
  let totalC = 0;
  for (const dose of doses) {
    const t = (now.getTime() - new Date(dose.date).getTime()) / (1000 * 60 * 60); // hours since dose
    if (t < 0) continue; // skip future doses
    const D = dose.doseAmount;
    // PK equation
    const C = (F * D * ka) / (Vd * (ka - ke)) * (Math.exp(-ke * t) - Math.exp(-ka * t));
    totalC += C;
  }
  return totalC; // mg/L
}

function getConcentrationCurve(doses: Dose[], start: Date, days: number, intervalHours: number = 12) {
  const points: { time: string; timestamp: number; concentration: number; isDose?: boolean; doseAmount?: number }[] = [];
  
  // Add dose markers
  doses.forEach(dose => {
    const doseTime = new Date(dose.date);
    if (doseTime >= start) {
      const point = {
        time: doseTime.toISOString().slice(0, 16),
        timestamp: doseTime.getTime(),
        concentration: calculateConcentration(doses, doseTime),
        isDose: true,
        doseAmount: dose.doseAmount
      };
      points.push(point);
    }
  });

  // Add concentration points
  for (let h = 0; h <= days * 24; h += intervalHours) {
    const time = new Date(start.getTime() + h * 60 * 60 * 1000);
    const conc = calculateConcentration(doses, time);
    points.push({ time: time.toISOString().slice(0, 16), timestamp: time.getTime(), concentration: conc });
  }

  // Sort points by time
  const sortedPoints = points.sort((a, b) => a.timestamp - b.timestamp);
  return sortedPoints;
}

// Generate forecast doses and curve for all steps (moved outside component for stable reference)
function generateForecastDoses(forecastStart: string, forecastSteps: any[]) {
  if (!forecastStart) return [];
  const doses = [];
  let lastDoseDate = new Date(forecastStart);
  let doseIndex = 0;
  for (let stepIdx = 0; stepIdx < forecastSteps.length; stepIdx++) {
    const step = forecastSteps[stepIdx];
    const doseMg = (step.penStrength / 60) * step.clicks;
    for (let i = 0; i < step.numDoses; i++) {
      const doseDate = new Date(lastDoseDate.getTime());
      doses.push({
        _id: `forecast-${doseIndex}`,
        penStrength: step.penStrength,
        clicks: step.clicks,
        doseAmount: doseMg,
        date: doseDate.toISOString(),
        remainingClicks: 0,
        remainingDoses: 0,
        penId: 'forecast',
        penName: 'Forecast'
      });
      // Advance lastDoseDate by the step's frequency for the next dose
      lastDoseDate = new Date(lastDoseDate.getTime() + step.frequency * 24 * 60 * 60 * 1000);
      doseIndex++;
    }
  }
  return doses;
}

function formatLocalDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB', { hour12: true, hour: '2-digit', minute: '2-digit' });
}

const PKCurve: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [doseHistory, setDoseHistory] = useState<Dose[]>([]);
  const [curveData, setCurveData] = useState<{ time: string; timestamp: number; concentration: number; isDose?: boolean; doseAmount?: number }[]>([]);

  // Multi-step forecast state
  const penStrengthOptions = PEN_STRENGTHS;
  type ForecastStep = {
    penStrength: number;
    clicks: number;
    frequency: number;
    numDoses: number;
  };
  const [forecastSteps, setForecastSteps] = useState<ForecastStep[]>([
    { penStrength: 10, clicks: 20, frequency: 7, numDoses: 4 }
  ]);
  const [forecastStart, setForecastStart] = useState<string>("");
  const [forecastCurve, setForecastCurve] = useState<{ time: string; timestamp: number; concentration: number; isDose?: boolean; doseAmount?: number }[]>([]);

  useEffect(() => {
    const fetchDoseHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('PKCurve: Fetching dose history with token:', token ? 'present' : 'missing');
        
        if (!token) {
          console.error('PKCurve: No authentication token found');
          return;
        }

        console.log('PKCurve: Making API request to:', `${API_BASE_URL}/doses`);
        const response = await fetch(`${API_BASE_URL}/doses`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Accept': 'application/json'
          },
          credentials: 'include'
        });

        console.log('PKCurve: Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('PKCurve: Error response:', errorText);
          throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
        }

        const data = await response.json();
        console.log('PKCurve: Raw dose history data:', data);

        if (!Array.isArray(data)) {
          console.error('PKCurve: Dose history data is not an array:', data);
          return;
        }

        if (data.length === 0) {
          console.log('PKCurve: No doses found in history');
        }

        setDoseHistory(data);
      } catch (error) {
        console.error('PKCurve: Error fetching dose history:', error);
        if (error instanceof Error) {
          console.error('PKCurve: Error details:', error.message);
          console.error('PKCurve: Error stack:', error.stack);
        }
      }
    };

    if (isAuthenticated) {
      console.log('PKCurve: User is authenticated, fetching dose history...');
      fetchDoseHistory();
    } else {
      console.log('PKCurve: User is not authenticated');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (doseHistory.length > 0) {
      console.log('PKCurve: Processing dose history for curve:', doseHistory);
      // Start from 7 days before the first dose, plot for 60 days after the last dose
      const firstDose = new Date(doseHistory[doseHistory.length - 1].date);
      const lastDose = new Date(doseHistory[0].date);
      const start = new Date(firstDose.getTime() - 7 * 24 * 60 * 60 * 1000);
      const days = Math.ceil((lastDose.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 60;
      console.log('PKCurve: Date range:', { start, lastDose, days });
      const curveData = getConcentrationCurve(doseHistory, start, days, 12);
      console.log('PKCurve: Generated curve data:', curveData);
      setCurveData(curveData);
    } else {
      console.log('PKCurve: No dose history available');
      setCurveData([]);
    }
  }, [doseHistory]);

  // Add a debug effect to monitor curve data changes
  useEffect(() => {
    console.log('PKCurve: Curve data updated:', curveData);
    console.log('PKCurve: Dose points in curve data:', curveData.filter(d => d.isDose));
  }, [curveData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!forecastStart) {
      setForecastCurve([]);
      return;
    }
    const forecastDoses = generateForecastDoses(forecastStart, forecastSteps);
    if (forecastDoses.length === 0) {
      setForecastCurve([]);
      return;
    }
    // Combine real and forecast doses
    const allDoses = [...doseHistory, ...forecastDoses];
    // Start plotting from the first forecast dose, for the total number of forecast doses * max frequency + 21 days
    const totalForecastDays = forecastDoses.length > 0 ? (forecastDoses.length * Math.max(...forecastSteps.map(s => s.frequency))) + 21 : 60;
    const start = new Date(forecastDoses[0].date);
    const curve = getConcentrationCurve(allDoses, start, totalForecastDays, 12);
    setForecastCurve(curve);
  }, [forecastSteps, forecastStart, doseHistory]);

  // Interpolate a curve onto a given timeline
  function interpolateCurve(curve: { timestamp: number; concentration: number }[], timeline: number[]): (number | undefined)[] {
    if (curve.length === 0) return timeline.map(() => undefined);
    let result: (number | undefined)[] = [];
    for (let i = 0; i < timeline.length; i++) {
      const t = timeline[i];
      // Find the two points in curve that t is between
      let prev = undefined, next = undefined;
      for (let j = 0; j < curve.length; j++) {
        if (curve[j].timestamp <= t) prev = curve[j];
        if (curve[j].timestamp >= t) {
          next = curve[j];
          break;
        }
      }
      if (prev && next && prev.timestamp !== next.timestamp) {
        // Linear interpolation
        const frac = (t - prev.timestamp) / (next.timestamp - prev.timestamp);
        result.push(prev.concentration + frac * (next.concentration - prev.concentration));
      } else if (prev && prev.timestamp === t) {
        result.push(prev.concentration);
      } else if (next && next.timestamp === t) {
        result.push(next.concentration);
      } else {
        result.push(undefined);
      }
    }
    return result;
  }

  // Build a unified timeline (every 12 hours from min to max timestamp)
  const allTimestampsRaw = [
    ...(curveData.length > 0 ? curveData.map(d => d.timestamp) : []),
    ...(forecastCurve.length > 0 ? forecastCurve.map(d => d.timestamp) : [])
  ];
  let timeline: number[] = [];
  if (allTimestampsRaw.length > 0) {
    const minTs = Math.min(...allTimestampsRaw);
    const maxTs = Math.max(...allTimestampsRaw);
    for (let t = minTs; t <= maxTs; t += 12 * 60 * 60 * 1000) {
      timeline.push(t);
    }
  }

  // Interpolate both curves onto the timeline
  const actualInterp = interpolateCurve(
    curveData.map(d => ({ timestamp: d.timestamp, concentration: d.concentration })),
    timeline
  );
  const forecastInterp = interpolateCurve(
    forecastCurve.map(d => ({ timestamp: d.timestamp, concentration: d.concentration })),
    timeline
  );

  // Build merged chart data for plotting and tooltip
  const mergedChartData = timeline.map((ts, i) => {
    // Find if this is a dose point in either curve
    const actualDose = curveData.find(d => d.timestamp === ts && d.isDose);
    const forecastDose = forecastCurve.find(d => d.timestamp === ts && d.isDose);
    return {
      timestamp: ts,
      time: actualDose?.time || forecastDose?.time || new Date(ts).toISOString().slice(0, 16),
      concentration: actualInterp[i],
      forecastConcentration: forecastInterp[i],
      isDose: actualDose?.isDose || forecastDose?.isDose,
      doseAmount: actualDose?.doseAmount || forecastDose?.doseAmount
    };
  });

  if (!isAuthenticated) {
    return <div className="App"><h2>Please log in to view your PK curve.</h2></div>;
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Find actual and forecast concentrations in payload
      const actual = payload.find((p: any) => p.name && p.name.toLowerCase().includes('actual'));
      const forecast = payload.find((p: any) => p.name && p.name.toLowerCase().includes('forecast'));
      // Use the first available payload for time/dose info
      const data = payload[0]?.payload;
      return (
        <div style={{
          backgroundColor: 'var(--background-light)',
          padding: '15px',
          border: '2px solid var(--primary-color)',
          borderRadius: '12px',
          boxShadow: '0 6px 12px var(--shadow-color)',
          minWidth: 220,
          maxWidth: 300
        }}>
          <p style={{ margin: '0 0 10px 0', color: 'var(--text-primary)', fontWeight: 700, fontSize: '1.1rem' }}>
            <b>Time:</b> {formatLocalDateTime(label)}
          </p>
          {forecast && (
            <p style={{ margin: '0 0 10px 0', color: 'var(--accent-color)', fontWeight: 600 }}>
              Forecast: <b>{forecast.value.toFixed(3)} mg/L</b>
            </p>
          )}
          {actual && (
            <p style={{ margin: '0 0 10px 0', color: 'var(--primary-color)', fontWeight: 600 }}>
              Actual: <b>{actual.value.toFixed(3)} mg/L</b>
            </p>
          )}
          {data?.isDose && (
            <p style={{ margin: '0', color: 'var(--text-secondary)', fontStyle: 'italic', borderTop: '1px solid var(--border-color)', paddingTop: '8px' }}>
              Dose: {data.doseAmount} mg
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Compute 7-day tick positions for the X axis based on the full data range (real + forecast)
  let xTicks: number[] = [];
  const allChartData = (curveData.length > 0 ? curveData : []).concat(forecastCurve.length > 0 ? forecastCurve : []);
  if (allChartData.length > 0) {
    const startDate = allChartData[0].timestamp;
    const endDate = allChartData[allChartData.length - 1].timestamp;
    let tickTime = startDate;
    while (tickTime <= endDate) {
      xTicks.push(tickTime);
      tickTime += 7 * 24 * 60 * 60 * 1000;
    }
  }

  // Compute y-axis domain to avoid clipping
  let yMin = 0;
  let yMax = 1;
  const allConcs = [
    ...curveData.map(d => d.concentration),
    ...forecastCurve.map(d => d.concentration)
  ];
  if (allConcs.length > 0) {
    yMin = Math.min(...allConcs, 0);
    yMax = Math.max(...allConcs) * 1.02; // add 2% headroom above actual max
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Mounjaro Blood Concentration Curve</h1>
        <p className="info-panel">This graph shows the predicted blood concentration of Mounjaro over time, based on your dose history.</p>
        
        {/* Forecast input form */}
        <div className="counter-container" style={{ width: '95%', maxWidth: '1200px' }}>
          <h2>Forecast Dosing Regimen</h2>
          <div className="flex flex-col gap-4 mb-4">
            {forecastSteps.map((step, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600 dark:text-gray-400">Pen strength (mg)</label>
                  <select 
                    value={step.penStrength} 
                    onChange={e => {
                      const newSteps = [...forecastSteps];
                      newSteps[idx].penStrength = Number(e.target.value);
                      setForecastSteps(newSteps);
                    }} 
                    className="p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base w-24"
                  >
                    {penStrengthOptions.map((opt: number) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600 dark:text-gray-400">Clicks</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="60" 
                    value={step.clicks} 
                    onChange={e => {
                      const newSteps = [...forecastSteps];
                      newSteps[idx].clicks = Number(e.target.value);
                      setForecastSteps(newSteps);
                    }} 
                    className="p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base w-20"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600 dark:text-gray-400">Dose</label>
                  <span className="p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 text-base font-semibold w-28 text-center">
                    {((step.penStrength / 60) * step.clicks).toFixed(2)} mg
                  </span>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600 dark:text-gray-400">Frequency (days)</label>
                  <input 
                    type="number" 
                    min="1" 
                    value={step.frequency} 
                    onChange={e => {
                      const newSteps = [...forecastSteps];
                      newSteps[idx].frequency = Number(e.target.value);
                      setForecastSteps(newSteps);
                    }} 
                    className="p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base w-20"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600 dark:text-gray-400">Number of doses</label>
                  <input 
                    type="number" 
                    min="1" 
                    value={step.numDoses} 
                    onChange={e => {
                      const newSteps = [...forecastSteps];
                      newSteps[idx].numDoses = Number(e.target.value);
                      setForecastSteps(newSteps);
                    }} 
                    className="p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base w-20"
                  />
                </div>

                {idx === 0 && (
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-600 dark:text-gray-400">Start date/time</label>
                    <input 
                      type="datetime-local" 
                      value={forecastStart} 
                      onChange={e => setForecastStart(e.target.value)} 
                      className="p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-base w-48"
                    />
                  </div>
                )}

                {forecastSteps.length > 1 && (
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-600 dark:text-gray-400 opacity-0">Remove</label>
                    <button 
                      type="button" 
                      onClick={() => {
                        setForecastSteps(forecastSteps.filter((_, i) => i !== idx));
                      }} 
                      className="px-4 py-2 rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors duration-200 text-sm w-20"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button 
            type="button" 
            onClick={() => setForecastSteps([...forecastSteps, { penStrength: 10, clicks: 20, frequency: 7, numDoses: 4 }])} 
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200 text-base mb-4"
          >
            Add Step
          </button>
        </div>
        
        <div className="info-box" style={{ width: '95%', maxWidth: '1200px', padding: '1rem' }}>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={mergedChartData} margin={{ top: 20, right: 30, left: 60, bottom: 0 }}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={true}
                horizontal={true}
                stroke="var(--border-color)"
              />
              <XAxis 
                type="number"
                dataKey="timestamp"
                minTickGap={40}
                tickFormatter={t => formatLocalDateTime(t)}
                domain={['dataMin', 'dataMax']}
                ticks={xTicks}
                allowDuplicatedCategory={false}
                stroke="var(--text-secondary)"
              />
              <YAxis 
                label={{ 
                  value: 'Blood Concentration (mg/L)', 
                  angle: -90, 
                  position: 'insideLeft', 
                  offset: -20, 
                  style: { textAnchor: 'middle', fill: 'var(--text-secondary)' } 
                }}
                domain={[yMin, yMax]}
                tickFormatter={v => v?.toPrecision(2)}
                stroke="var(--text-secondary)"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {/* Real PK curve */}
              {curveData.length > 0 && (
                <Line 
                  type="monotone" 
                  dataKey="concentration" 
                  stroke="var(--primary-color)" 
                  dot={false} 
                  name="Actual Concentration (mg/L)" 
                />
              )}
              {/* Forecast PK curve */}
              {forecastCurve.length > 0 && (
                <Line 
                  type="monotone" 
                  dataKey="forecastConcentration" 
                  stroke="var(--accent-color)" 
                  strokeDasharray="6 3"
                  dot={false}
                  name="Forecast Concentration (mg/L)" 
                />
              )}
              {/* Dose markers for real doses */}
              {curveData.filter(d => d.isDose).map((dose, index) => (
                <ReferenceDot
                  key={index}
                  x={dose.timestamp}
                  y={dose.concentration}
                  r={6}
                  fill="var(--primary-color)"
                  stroke="var(--background-light)"
                  strokeWidth={2}
                  isFront={true}
                />
              ))}
              {/* Dose markers for forecast doses (hollow) */}
              {forecastCurve.filter(d => d.isDose).map((dose, index) => (
                <ReferenceDot
                  key={`forecast-dot-${index}`}
                  x={dose.timestamp}
                  y={dose.concentration}
                  r={6}
                  fill="var(--background-lighter)"
                  stroke="var(--accent-color)"
                  strokeWidth={2}
                  isFront={true}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </header>
    </div>
  );
};

export default PKCurve; 